import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertTriangle, FileCode, RefreshCw, Save } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CopyButton } from '@/components/ui/CopyButton';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useRegenerateSshConfig, useSshConfig } from '@/hooks/useSshConfig';
import { formatDateTime } from '@/lib/format';

/**
 * SSH-Config-Preview (WT-303, Sprint 3).
 *
 * - Read-only Code-Block der aktuellen `~/.ssh/config.d/wartungstool.conf`
 * - "Regenerieren"-Button (mit Confirm) schreibt neu aus DB-Inventar
 * - Statistik: X Hosts gerendert / Y übersprungen
 * - Liste der Skip-Reasons (z.B. "kein SSH-Profil", "Profil deaktiviert")
 */

export const Route = createFileRoute('/settings/ssh-config')({
  component: SshConfigPage,
});

function SshConfigPage(): JSX.Element {
  const stateQuery = useSshConfig();
  const regenerate = useRegenerateSshConfig();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onRegenerate = (): void => {
    setConfirmOpen(false);
    regenerate.mutate({ dryRun: false });
  };

  const state = stateQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">SSH-Konfig</h1>
          <p className="text-muted">
            Generierte ssh-config aus Inventar – Pfad im Container:{' '}
            <code className="rounded bg-surface-2 px-1 text-xs">
              /home/app/.ssh/config.d/wartungstool.conf
            </code>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void stateQuery.refetch()}
            loading={stateQuery.isFetching}
          >
            Aktualisieren
          </Button>
          <Button
            leftIcon={<Save className="size-4" />}
            onClick={() => setConfirmOpen(true)}
            loading={regenerate.isPending}
          >
            Regenerieren
          </Button>
        </div>
      </header>

      {stateQuery.isLoading && <SkeletonRows rows={4} cols={2} />}
      {stateQuery.isError && <ErrorAlert error={stateQuery.error} />}
      {regenerate.isError && <ErrorAlert error={regenerate.error} />}

      {state && (
        <>
          <Card>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Stat label="Hosts gerendert" value={state.host_count} tone="success" />
              <Stat
                label="Profile übersprungen"
                value={state.skipped_count ?? 0}
                tone={(state.skipped_count ?? 0) > 0 ? 'warning' : 'neutral'}
                hint={(state.skipped_reasons ?? []).slice(0, 3).join(' · ')}
              />
              <Stat
                label="Letzte Generation"
                rawValue={formatDateTime(state.last_generated_at ?? null)}
                tone="neutral"
              />
            </div>
            {state.warnings && state.warnings.length > 0 && (
              <ul className="mt-3 flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
                {state.warnings.map((w, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <AlertTriangle
                      className="mt-0.5 size-3 text-warning"
                      aria-hidden="true"
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title={
              <span className="flex items-center gap-2">
                <FileCode className="size-5 text-muted" aria-hidden="true" />
                <span>Aktueller Inhalt</span>
                <Badge tone="neutral">read-only</Badge>
              </span>
            }
            description={state.path}
            actions={<CopyButton value={state.content}>Kopieren</CopyButton>}
          >
            <pre
              className="max-h-[60vh] overflow-auto rounded-md border border-border bg-surface-2 p-3 text-xs"
              aria-label="ssh-config Inhalt"
            >
              {state.content || '— leer —'}
            </pre>
          </Card>

          {regenerate.data?.diff && (
            <Card
              title="Änderungen seit letzter Generation"
              description="Unified-Diff der letzten regenerate-Operation."
            >
              <pre className="max-h-72 overflow-auto rounded-md border border-border bg-surface-2 p-3 text-xs">
                {regenerate.data.diff}
              </pre>
            </Card>
          )}
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="ssh-config regenerieren?"
        description="Schreibt ~/.ssh/config.d/wartungstool.conf neu aus dem aktuellen DB-Inventar. Eine Backup-Kopie der vorherigen Version wird angelegt."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              leftIcon={<Save className="size-4" />}
              onClick={onRegenerate}
              loading={regenerate.isPending}
            >
              Regenerieren
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          Achtung: laufende ssh-Verbindungen werden nicht abgebrochen, aber neue
          Verbindungen verwenden die neue Config (inkl. Host-Aliase).
        </p>
      </Dialog>
    </div>
  );
}

function Stat({
  label,
  value,
  rawValue,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value?: number;
  rawValue?: string;
  tone?: 'success' | 'warning' | 'danger' | 'neutral';
  hint?: string;
}): JSX.Element {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-text';
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p className={['mt-1 text-2xl font-semibold', toneClass].join(' ')}>
        {value != null ? value : (rawValue ?? '–')}
      </p>
      {hint && <p className="mt-1 truncate text-xs text-muted">{hint}</p>}
    </div>
  );
}
